import type {
  DocumentType,
  Member,
  MemberDocument,
  MemberDraft,
  MemberGroupSummary,
  MemberGuarantor,
  MemberQuery,
  MemberStatus,
  ProofType,
  AdvanceDepositInput,
  AdvanceWithdrawInput,
  MemberAccountTransaction,
} from "@/services/types";
import { http, httpUpload, httpBlob, authStorage } from "@/services/http";
import { API_BASE_URL } from "@/services/config";

/**
 * Centralized member service connecting directly to the backend REST API.
 */

/* ================= Real API — backend shapes and mapping ================= */

interface BackendMember {
  id: string;
  memberCode: string;
  fullName: string;
  fatherOrSpouse: string | null;
  phone: string;
  altPhone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  idProofType: string | null;
  idProofNumber: string | null;
  bankAccountName: string | null;
  bankAccountNo: string | null;
  bankIfsc: string | null;
  upiId: string | null;
  isOrganizer: boolean;
  accountBalance?: string | number;
  status: "ACTIVE" | "INACTIVE" | "BLACKLISTED";
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  enrollments?: BackendEnrollmentRow[];
}

interface BackendEnrollmentRow {
  groupId: string;
  groupCode: string;
  groupName: string;
  ticketNo: number;
  isOrganizer: boolean;
  hasWon: boolean;
  wonCycleNo: number | null;
  enrollmentStatus: string;
  joinedOn: string;
  installmentAmount: string;
  outstanding: string;
}

interface BackendDocument {
  id: string;
  docType: "AGREEMENT" | "ID_PROOF" | "ADDRESS_PROOF" | "PHOTO" | "OTHER";
  fileName: string;
  fileSize: string | number;
  mimeType: string;
  uploadedAt: string;
}

interface BackendGuarantor {
  id: string;
  guarantorName: string;
  relation: string | null;
  phone: string;
  address: string | null;
  idProofNumber: string | null;
  linkedMemberId: string | null;
}

interface BackendGroupItem {
  id: string;
  groupName: string;
  installmentAmount: string | number;
}

function mapStatusFromBackend(status?: BackendMember["status"]): MemberStatus {
  return (status ? status.toLowerCase() : "active") as MemberStatus;
}

function mapMember(
  bm: BackendMember,
  extras: { enrollments?: Member["enrollments"]; documents?: MemberDocument[]; guarantors?: MemberGuarantor[] } = {}
): Member {
  return {
    id: bm.id,
    memberCode: bm.memberCode,
    fullName: bm.fullName,
    guardianName: bm.fatherOrSpouse ?? "",
    primaryPhone: bm.phone,
    alternatePhone: bm.altPhone ?? "",
    email: bm.email ?? "",
    addressLine1: bm.addressLine1 ?? "",
    addressLine2: bm.addressLine2 ?? "",
    city: bm.city ?? "",
    state: bm.state ?? "",
    pinCode: bm.pincode ?? "",
    proofType: ((bm.idProofType?.toLowerCase() as ProofType) || "") as ProofType | "",
    proofNumber: bm.idProofNumber ?? "",
    bankAccountName: bm.bankAccountName ?? "",
    bankAccountNumber: bm.bankAccountNo ?? "",
    ifsc: bm.bankIfsc ?? "",
    upiId: bm.upiId ?? "",
    isOrganizer: Boolean(bm.isOrganizer),
    accountBalance: Number(bm.accountBalance ?? (bm as any).account_balance ?? 0),
    status: mapStatusFromBackend(bm.status),
    notes: bm.notes ?? "",
    enrollments: extras.enrollments ?? [],
    documents: extras.documents ?? [],
    guarantors: extras.guarantors ?? [],
    createdBy: bm.createdBy ?? "",
    createdAt: bm.createdAt,
    updatedBy: bm.createdBy ?? "",
    updatedAt: bm.updatedAt,
  };
}

function mapEnrollment(row: BackendEnrollmentRow): Member["enrollments"][number] {
  const status = row.hasWon ? "completed" : row.enrollmentStatus === "DEFAULTED" ? "blacklisted" : "active";
  return {
    groupId: row.groupId,
    groupName: row.groupName,
    ticketNumber: String(row.ticketNo),
    monthlyInstallment: Number(row.installmentAmount) || 0,
    joinedOn: row.joinedOn,
    wonCycle: row.hasWon ? row.wonCycleNo : null,
    wonOn: null,
    outstanding: Number(row.outstanding) || 0,
    status,
  };
}

function mapDocumentFromBackend(row: BackendDocument, memberProofType: ProofType | ""): MemberDocument {
  const docType: DocumentType =
    row.docType === "PHOTO"
      ? "photo"
      : row.docType === "ADDRESS_PROOF"
        ? "address"
        : row.docType === "ID_PROOF"
          ? memberProofType || "other"
          : "other";
  return {
    id: row.id,
    docType,
    fileName: row.fileName,
    mimeType: row.mimeType,
    size: Number(row.fileSize) || 0,
    uploadedOn: row.uploadedAt,
  };
}

function mapDocumentTypeToBackend(docType: DocumentType): BackendDocument["docType"] {
  if (docType === "photo") return "PHOTO";
  if (docType === "address") return "ADDRESS_PROOF";
  if (docType === "other") return "OTHER";
  return "ID_PROOF"; // aadhaar | pan | voter | driving | passport
}

function mapGuarantorFromBackend(row: BackendGuarantor): MemberGuarantor {
  return {
    id: row.id,
    name: row.guarantorName,
    relationship: row.relation ?? "",
    phone: row.phone,
    address: row.address ?? "",
    linkedMemberId: row.linkedMemberId,
  };
}

function draftToBackendBody(draft: MemberDraft): Record<string, unknown> {
  return {
    fullName: draft.fullName,
    phone: draft.primaryPhone,
    fatherOrSpouse: draft.guardianName || undefined,
    altPhone: draft.alternatePhone || undefined,
    email: draft.email || undefined,
    addressLine1: draft.addressLine1 || undefined,
    addressLine2: draft.addressLine2 || undefined,
    city: draft.city || undefined,
    state: draft.state || undefined,
    pincode: draft.pinCode || undefined,
    idProofType: draft.proofType || undefined,
    idProofNumber: draft.proofNumber || undefined,
    bankAccountName: draft.bankAccountName || undefined,
    bankAccountNo: draft.bankAccountNumber || undefined,
    bankIfsc: draft.ifsc || undefined,
    upiId: draft.upiId || undefined,
    isOrganizer: draft.isOrganizer,
    notes: draft.notes || undefined,
  };
}

async function fetchEnrollments(memberId: string): Promise<Member["enrollments"]> {
  try {
    const rows = await http<BackendEnrollmentRow[]>(`/members/${memberId}/groups`, { method: "GET" });
    return (rows ?? []).map(mapEnrollment);
  } catch {
    return [];
  }
}

/* ================= Public service functions ================= */

export async function listGroups(): Promise<MemberGroupSummary[]> {
  try {
    const envelope = await http<{ data: BackendGroupItem[] }>("/groups?pageSize=100", {
      method: "GET",
      raw: true,
    });
    const groups = envelope.data ?? [];
    return groups.map((g) => ({
      id: g.id,
      name: g.groupName,
      monthlyAmount: Number(g.installmentAmount) || 0,
    }));
  } catch {
    return [];
  }
}

export async function listMembers(query: MemberQuery = {}): Promise<Member[]> {
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.status) params.set("status", query.status.toUpperCase());
  if (query.groupId) params.set("groupId", query.groupId);
  if (query.hasDues) params.set("hasOutstanding", "true");
  params.set("pageSize", "100");

  let page = 1;
  let all: BackendMember[] = [];
  for (;;) {
    params.set("page", String(page));
    const envelope = await http<{ data: BackendMember[]; meta: { totalPages: number } }>(
      `/members?${params.toString()}`,
      { method: "GET", raw: true }
    );
    all = all.concat(envelope.data ?? []);
    if (!envelope.meta || page >= envelope.meta.totalPages || envelope.meta.totalPages === 0) break;
    page += 1;
  }

  return all.map((bm) =>
    mapMember(bm, {
      enrollments: Array.isArray(bm.enrollments) ? bm.enrollments.map(mapEnrollment) : [],
    })
  );
}

export async function getMember(id: string): Promise<Member> {
  const [bm, enrollments, documentRows, guarantorRows] = await Promise.all([
    http<BackendMember>(`/members/${id}`, { method: "GET" }),
    fetchEnrollments(id),
    http<BackendDocument[]>(`/members/${id}/documents`, { method: "GET" }).catch(() => []),
    http<BackendGuarantor[]>(`/members/${id}/guarantors`, { method: "GET" }).catch(() => []),
  ]);
  const proofType = ((bm.idProofType?.toLowerCase() as ProofType) || "") as ProofType | "";
  return mapMember(bm, {
    enrollments,
    documents: (documentRows ?? []).map((d) => mapDocumentFromBackend(d, proofType)),
    guarantors: (guarantorRows ?? []).map(mapGuarantorFromBackend),
  });
}

/** Returns the first member matching a normalized phone, or null. Used for duplicate warnings. */
export async function getMemberByPhone(phone: string): Promise<Member | null> {
  const normalized = phone.replace(/\D/g, "");
  if (!/^[6-9]\d{9}$/.test(normalized)) return null;
  try {
    const result = await http<{ matches: { id: string }[] }>(
      `/members/check-duplicate?phone=${normalized}`,
      { method: "GET" }
    );
    if (!result?.matches || result.matches.length === 0) return null;
    return await getMember(result.matches[0].id);
  } catch {
    return null;
  }
}

/** Search across name / phone / code, used by the guarantor-link member selector. */
export async function searchMembers(query: string, excludeId?: string): Promise<Member[]> {
  const q = query.trim();
  if (!q) return [];

  const rows = await http<BackendMember[]>(`/members?search=${encodeURIComponent(q)}&pageSize=10`, {
    method: "GET",
  });
  return (Array.isArray(rows) ? rows : [])
    .filter((bm) => bm.id !== excludeId)
    .map((bm) => mapMember(bm));
}

export async function createMember(draft: MemberDraft, _actor: string): Promise<Member> {
  const bm = await http<BackendMember>("/members", { method: "POST", body: draftToBackendBody(draft) });
  return mapMember(bm);
}

export async function updateMember(id: string, draft: MemberDraft, _actor: string): Promise<Member> {
  await http<BackendMember>(`/members/${id}`, { method: "PATCH", body: draftToBackendBody(draft) });
  return getMember(id); // re-fetch: keeps enrollments/documents/guarantors populated
}

export async function updateMemberStatus(
  id: string,
  status: MemberStatus,
  reason: string,
  _actor: string
): Promise<Member> {
  await http<BackendMember>(`/members/${id}/status`, {
    method: "PATCH",
    body: { status: status.toUpperCase(), reason: reason || undefined },
  });
  return getMember(id);
}

export async function addDocument(memberId: string, file: File, docType: DocumentType): Promise<Member> {
  const formData = new FormData();
  formData.append("docType", mapDocumentTypeToBackend(docType));
  formData.append("file", file);
  await httpUpload<BackendDocument>(`/members/${memberId}/documents`, formData);
  return getMember(memberId);
}

export async function removeDocument(memberId: string, documentId: string): Promise<Member> {
  await http<void>(`/documents/${documentId}`, { method: "DELETE" });
  return getMember(memberId);
}

export async function getDocumentBlob(documentId: string): Promise<Blob> {
  return httpBlob(`/documents/${documentId}/file`);
}

export function getDocumentDirectUrl(documentId: string): string {
  const token = authStorage.get();
  return `${API_BASE_URL}/api/v1/documents/${documentId}/file${token ? `?token=${encodeURIComponent(token)}` : ""}`;
}

export async function downloadDocument(documentId: string, fileName: string): Promise<void> {
  const blob = await httpBlob(`/documents/${documentId}/file?download=true`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function addGuarantor(memberId: string, guarantor: MemberGuarantor): Promise<Member> {
  await http<BackendGuarantor>(`/members/${memberId}/guarantors`, {
    method: "POST",
    body: {
      guarantorName: guarantor.name,
      relation: guarantor.relationship || undefined,
      phone: guarantor.phone,
      address: guarantor.address || undefined,
      linkedMemberId: guarantor.linkedMemberId || undefined,
    },
  });
  return getMember(memberId);
}

export async function removeGuarantor(memberId: string, guarantorId: string): Promise<Member> {
  await http<void>(`/guarantors/${guarantorId}`, { method: "DELETE" });
  return getMember(memberId);
}

export async function deleteMember(id: string): Promise<void> {
  await http<void>(`/members/${id}`, { method: "DELETE" });
}

export async function depositAdvance(
  memberId: string,
  input: AdvanceDepositInput
): Promise<{ transaction: MemberAccountTransaction; member: Member }> {
  const backendMode = input.mode.toUpperCase();
  const res = await http<{ transaction: MemberAccountTransaction; member: BackendMember }>(
    `/members/${memberId}/account/deposit`,
    {
      method: "POST",
      body: {
        amount: input.amount,
        mode: backendMode,
        referenceNo: input.referenceNo?.trim() || undefined,
        receivedOn: input.receivedOn || undefined,
        remarks: input.remarks?.trim() || undefined,
      },
    }
  );
  return {
    transaction: {
      ...res.transaction,
      amount: Number(res.transaction.amount),
      balanceBefore: Number(res.transaction.balanceBefore),
      balanceAfter: Number(res.transaction.balanceAfter),
      mode: (res.transaction.mode ? res.transaction.mode.toLowerCase() : "cash") as any,
    },
    member: mapMember(res.member),
  };
}

export async function withdrawAdvance(
  memberId: string,
  input: AdvanceWithdrawInput
): Promise<{ transaction: MemberAccountTransaction; member: Member }> {
  const backendMode = input.mode.toUpperCase();
  const res = await http<{ transaction: MemberAccountTransaction; member: BackendMember }>(
    `/members/${memberId}/account/withdraw`,
    {
      method: "POST",
      body: {
        amount: input.amount,
        mode: backendMode,
        referenceNo: input.referenceNo?.trim() || undefined,
        withdrawnOn: input.withdrawnOn || undefined,
        remarks: input.remarks?.trim() || undefined,
      },
    }
  );
  return {
    transaction: {
      ...res.transaction,
      amount: Number(res.transaction.amount),
      balanceBefore: Number(res.transaction.balanceBefore),
      balanceAfter: Number(res.transaction.balanceAfter),
      mode: (res.transaction.mode ? res.transaction.mode.toLowerCase() : "cash") as any,
    },
    member: mapMember(res.member),
  };
}

export async function getAccountTransactions(
  memberId: string,
  params?: { page?: number; pageSize?: number; type?: string; dateFrom?: string; dateTo?: string }
): Promise<{ data: MemberAccountTransaction[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.pageSize) q.set("pageSize", String(params.pageSize));
  if (params?.type) q.set("type", params.type);
  if (params?.dateFrom) q.set("dateFrom", params.dateFrom);
  if (params?.dateTo) q.set("dateTo", params.dateTo);

  const qs = q.toString() ? `?${q.toString()}` : "";
  const res = await http<{ data: any[]; meta: { total: number } }>(`/members/${memberId}/account/transactions${qs}`);
  const list = res.data ?? (res as any);
  return {
    data: (Array.isArray(list) ? list : []).map((t: any) => ({
      ...t,
      amount: Number(t.amount),
      balanceBefore: Number(t.balanceBefore),
      balanceAfter: Number(t.balanceAfter),
      mode: (t.mode ? t.mode.toLowerCase() : "cash") as any,
    })),
    total: res.meta?.total ?? (Array.isArray(list) ? list.length : 0),
  };
}

export async function updateAccountTransaction(
  memberId: string,
  txId: string,
  input: {
    amount?: number;
    mode?: PaymentMode;
    referenceNo?: string;
    date?: string;
    remarks?: string;
  }
): Promise<{ transaction: MemberAccountTransaction; member: Member }> {
  const backendMode = input.mode ? input.mode.toUpperCase() : undefined;
  const res = await http<{ transaction: MemberAccountTransaction; member: BackendMember }>(
    `/members/${memberId}/account/transactions/${txId}`,
    {
      method: "PUT",
      body: {
        amount: input.amount,
        mode: backendMode,
        referenceNo: input.referenceNo !== undefined ? (input.referenceNo.trim() || null) : undefined,
        date: input.date || undefined,
        remarks: input.remarks !== undefined ? (input.remarks.trim() || null) : undefined,
      },
    }
  );
  return {
    transaction: {
      ...res.transaction,
      amount: Number(res.transaction.amount),
      balanceBefore: Number(res.transaction.balanceBefore),
      balanceAfter: Number(res.transaction.balanceAfter),
      mode: (res.transaction.mode ? res.transaction.mode.toLowerCase() : "cash") as any,
    },
    member: mapMember(res.member),
  };
}

export async function deleteAccountTransaction(
  memberId: string,
  txId: string
): Promise<{ success: boolean; member: Member }> {
  const res = await http<{ success: boolean; member: BackendMember }>(
    `/members/${memberId}/account/transactions/${txId}`,
    {
      method: "DELETE",
    }
  );
  return {
    success: res.success,
    member: mapMember(res.member),
  };
}

/** Convenience wrappers matching the service-layer response convention. */
export const memberService = {
  listGroups,
  listMembers,
  getMember,
  getMemberByPhone,
  searchMembers,
  createMember,
  updateMember,
  deleteMember,
  updateMemberStatus,
  addDocument,
  removeDocument,
  getDocumentBlob,
  getDocumentDirectUrl,
  downloadDocument,
  addGuarantor,
  removeGuarantor,
  depositAdvance,
  withdrawAdvance,
  getAccountTransactions,
  updateAccountTransaction,
  deleteAccountTransaction,
};

